import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UpgradePromptModal from '../../components/UpgradePromptModal/UpgradePromptModal';
import { useSubscription } from '../../context/SubscriptionContext';
import { categoriesApi, projectsApi } from '../../services/apiService';
import './ProjectInputPage.css';

const ProjectInputPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const { hasPermission, planDisplayName, loading: subscriptionLoading } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);
  
  // Check canSubmitProjects permission after subscription loads
  useEffect(() => {
    if (!subscriptionLoading && !hasPermission('canSubmitProjects')) {
      setShowUpgradeModal(true);
    }
  }, [hasPermission, subscriptionLoading]);

  const [activeMenu, setActiveMenu] = useState('projects');
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    category: '',
    subCategory: '',
    projectTitle: '',
    subTitle: '',
    description: '',
    projectType: '',
    budget: '',
    currency: 'USD',
    country: '',
    city: '',
    additionalInfo: [
      { id: 1, title: '', value: '' }
    ],
    mainImage: null,
    galleryImages: Array(8).fill(null),
    bannerImages: Array(8).fill(null)
  });

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await categoriesApi.getAll();
        setCategories(data);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Update subcategories when category changes
  useEffect(() => {
    if (formData.category) {
      const selectedCat = categories.find(c => c.name === formData.category);
      setSubcategories(selectedCat?.subcategories || []);
    } else {
      setSubcategories([]);
    }
  }, [formData.category, categories]);

  // Fetch project data in edit mode
  useEffect(() => {
    if (!editId) return;
    const fetchProject = async () => {
      setLoadingProject(true);
      try {
        const project = await projectsApi.getById(editId);
        // Find which category contains this subCategory
        let categoryName = '';
        if (project.subCategory && categories.length > 0) {
          const cat = categories.find(c =>
            c.subcategories?.some(sc => sc.name === project.subCategory)
          );
          if (cat) categoryName = cat.name;
        }
        // If projectType matches a category name, use that
        if (!categoryName && project.projectType) {
          const cat = categories.find(c => c.name === project.projectType);
          if (cat) categoryName = cat.name;
        }
        setFormData(prev => ({
          ...prev,
          category: categoryName || project.projectType || '',
          subCategory: project.subCategory || '',
          projectTitle: project.title || '',
          subTitle: project.description || '',
          description: project.description || '',
          projectType: project.projectType || '',
          budget: project.budget ? String(project.budget) : '',
          currency: project.currency || 'USD',
          country: project.country || '',
          city: project.city || '',
        }));
      } catch (err) {
        console.error('Failed to fetch project:', err);
      } finally {
        setLoadingProject(false);
      }
    };
    if (categories.length > 0) {
      fetchProject();
    }
  }, [editId, categories]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.projectTitle.trim()) {
      alert('Project title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: formData.projectTitle,
        description: formData.subTitle || formData.description || formData.projectTitle,
        projectType: formData.category || null,
        subCategory: formData.subCategory || null,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        currency: formData.currency,
        country: formData.country || null,
        city: formData.city || null,
      };
      if (editId) {
        await projectsApi.update(editId, payload);
      } else {
        await projectsApi.adminCreate(payload);
      }
      navigate('/project-manage');
    } catch (err) {
      console.error('Failed to save project:', err);
      alert('Failed to save project. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdditionalInfoChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      additionalInfo: prev.additionalInfo.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  const addAdditionalInfo = () => {
    setFormData(prev => ({
      ...prev,
      additionalInfo: [
        ...prev.additionalInfo,
        { id: Date.now(), title: '', value: '' }
      ]
    }));
  };

  const removeAdditionalInfo = (id) => {
    setFormData(prev => ({
      ...prev,
      additionalInfo: prev.additionalInfo.filter(item => item.id !== id)
    }));
  };

  const sidebarMenuItems = [
    { id: 'dashboard', icon: 'home', label: 'Dashboard', active: false },
    { id: 'projects', icon: 'list', label: 'Project Lists', active: true, hasSubmenu: true },
    { id: 'rfq', icon: 'bell', label: 'RFQ Manage', active: false, hasSubmenu: true },
    { id: 'messages', icon: 'chat', label: 'Inqs / Msgs', active: false, hasSubmenu: true },
    { id: 'membership', icon: 'membership', label: 'Membership Plan', active: false, hasSubmenu: true },
    { id: 'profile', icon: 'user', label: 'Specialist Profile', active: false },
  ];

  const renderIcon = (iconName) => {
    switch (iconName) {
      case 'home':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 22V12H15V22" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'list':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M3.25 7C3.25 6.80109 3.32902 6.61032 3.46967 6.46967C3.61032 6.32902 3.80109 6.25 4 6.25H20C20.1989 6.25 20.3897 6.32902 20.5303 6.46967C20.671 6.61032 20.75 6.80109 20.75 7C20.75 7.19891 20.671 7.38968 20.5303 7.53033C20.3897 7.67098 20.1989 7.75 20 7.75H4C3.80109 7.75 3.61032 7.67098 3.46967 7.53033C3.32902 7.38968 3.25 7.19891 3.25 7ZM3.25 12C3.25 11.8011 3.32902 11.6103 3.46967 11.4697C3.61032 11.329 3.80109 11.25 4 11.25H15C15.1989 11.25 15.3897 11.329 15.5303 11.4697C15.671 11.6103 15.75 11.8011 15.75 12C15.75 12.1989 15.671 12.3897 15.5303 12.5303C15.3897 12.671 15.1989 12.75 15 12.75H4C3.80109 12.75 3.61032 12.671 3.46967 12.5303C3.32902 12.3897 3.25 12.1989 3.25 12ZM3.25 17C3.25 16.8011 3.32902 16.6103 3.46967 16.4697C3.61032 16.329 3.80109 16.25 4 16.25H9C9.19891 16.25 9.38968 16.329 9.53033 16.4697C9.67098 16.6103 9.75 16.8011 9.75 17C9.75 17.1989 9.67098 17.3897 9.53033 17.5303C9.38968 17.671 9.19891 17.75 9 17.75H4C3.80109 17.75 3.61032 17.671 3.46967 17.5303C3.32902 17.3897 3.25 17.1989 3.25 17Z" fill="#505050"/>
          </svg>
        );
      case 'bell':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13.7295 21C13.5537 21.3031 13.3014 21.5547 12.9978 21.7295C12.6941 21.9044 12.3499 21.9965 11.9995 21.9965C11.6492 21.9965 11.3049 21.9044 11.0013 21.7295C10.6977 21.5547 10.4453 21.3031 10.2695 21" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'chat':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.25 7.5H17.25V4.5C17.25 4.10218 17.092 3.72064 16.8107 3.43934C16.5294 3.15804 16.1478 3 15.75 3H3.75C3.35218 3 2.97064 3.15804 2.68934 3.43934C2.40804 3.72064 2.25 4.10218 2.25 4.5V16.5C2.25044 16.6411 2.29068 16.7792 2.36608 16.8985C2.44149 17.0177 2.54901 17.1133 2.67629 17.1742C2.80358 17.2351 2.94546 17.2589 3.08564 17.2428C3.22581 17.2266 3.3586 17.1713 3.46875 17.0831L6.75 14.4375V17.25C6.75 17.6478 6.90804 18.0294 7.18934 18.3107C7.47064 18.592 7.85218 18.75 8.25 18.75H17.0241L20.5312 21.5831C20.664 21.6905 20.8293 21.7493 21 21.75C21.1989 21.75 21.3897 21.671 21.5303 21.5303C21.671 21.3897 21.75 21.1989 21.75 21V9C21.75 8.60218 21.592 8.22064 21.3107 7.93934C21.0294 7.65804 20.6478 7.5 20.25 7.5ZM6.23906 12.9169L3.75 14.9297V4.5H15.75V12.75H6.71063C6.53897 12.75 6.37252 12.8089 6.23906 12.9169ZM20.25 19.4297L17.7609 17.4169C17.6282 17.3095 17.4629 17.2507 17.2922 17.25H8.25V14.25H15.75C16.1478 14.25 16.5294 14.092 16.8107 13.8107C17.092 13.5294 17.25 13.1478 17.25 12.75V9H20.25V19.4297Z" fill="#333333"/>
          </svg>
        );
      case 'membership':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.625 18.5625H3C2.5526 18.562 2.12367 18.3841 1.80731 18.0677C1.49095 17.7513 1.313 17.3224 1.3125 16.875V4.875C1.3125 3.94463 2.06963 3.1875 3 3.1875H5.625C5.77418 3.1875 5.91726 3.24676 6.02275 3.35225C6.12824 3.45774 6.1875 3.60082 6.1875 3.75C6.1875 3.89918 6.12824 4.04226 6.02275 4.14775C5.91726 4.25324 5.77418 4.3125 5.625 4.3125H3C2.68988 4.3125 2.4375 4.56488 2.4375 4.875V16.875C2.4375 17.1851 2.68988 17.4375 3 17.4375H5.625C5.77418 17.4375 5.91726 17.4968 6.02275 17.6023C6.12824 17.7077 6.1875 17.8508 6.1875 18C6.1875 18.1492 6.12824 18.2923 6.02275 18.3977C5.91726 18.5032 5.77418 18.5625 5.625 18.5625ZM19.875 14.2875C19.7258 14.2875 19.5827 14.2282 19.4773 14.1227C19.3718 14.0173 19.3125 13.8742 19.3125 13.725V4.875C19.3125 4.56488 19.0601 4.3125 18.75 4.3125H16.125C15.9758 4.3125 15.8327 4.25324 15.7273 4.14775C15.6218 4.04226 15.5625 3.89918 15.5625 3.75C15.5625 3.60082 15.6218 3.45774 15.7273 3.35225C15.8327 3.24676 15.9758 3.1875 16.125 3.1875H18.75C19.6804 3.1875 20.4375 3.94463 20.4375 4.875V13.725C20.4375 13.8742 20.3782 14.0173 20.2727 14.1227C20.1673 14.2282 20.0242 14.2875 19.875 14.2875Z" fill="#333333"/>
            <path d="M11.25 20.4375H6.75C6.3026 20.437 5.87367 20.2591 5.55731 19.9427C5.24095 19.6263 5.063 19.1974 5.0625 18.75V3C5.0625 2.06963 5.81963 1.3125 6.75 1.3125H15C15.9304 1.3125 16.6875 2.06963 16.6875 3V13.3837C16.6875 13.5329 16.6282 13.676 16.5227 13.7815C16.4173 13.887 16.2742 13.9462 16.125 13.9462C15.9758 13.9462 15.8327 13.887 15.7273 13.7815C15.6218 13.676 15.5625 13.5329 15.5625 13.3837V3C15.5625 2.68988 15.3101 2.4375 15 2.4375H6.75C6.43988 2.4375 6.1875 2.68988 6.1875 3V18.75C6.1875 19.0601 6.43988 19.3125 6.75 19.3125H11.25C11.3992 19.3125 11.5423 19.3718 11.6477 19.4773C11.7532 19.5827 11.8125 19.7258 11.8125 19.875C11.8125 20.0242 11.7532 20.1673 11.6477 20.2727C11.5423 20.3782 11.3992 20.4375 11.25 20.4375Z" fill="#333333"/>
            <path d="M17.625 22.6875C14.8335 22.6875 12.5625 20.4165 12.5625 17.625C12.5625 14.8335 14.8335 12.5625 17.625 12.5625C20.4165 12.5625 22.6875 14.8335 22.6875 17.625C22.6875 20.4165 20.4165 22.6875 17.625 22.6875ZM17.625 13.6875C15.4538 13.6875 13.6875 15.4538 13.6875 17.625C13.6875 19.7963 15.4538 21.5625 17.625 21.5625C19.7963 21.5625 21.5625 19.7963 21.5625 17.625C21.5625 15.4538 19.7963 13.6875 17.625 13.6875Z" fill="#333333"/>
            <path d="M16.9691 19.687C16.82 19.687 16.677 19.6278 16.5716 19.5224L14.8841 17.8349C14.8294 17.7832 14.7857 17.7212 14.7555 17.6523C14.7253 17.5835 14.7091 17.5093 14.7081 17.4341C14.707 17.3589 14.721 17.2843 14.7493 17.2146C14.7776 17.145 14.8195 17.0817 14.8727 17.0285C14.9258 16.9753 14.9891 16.9333 15.0587 16.905C15.1284 16.8767 15.203 16.8627 15.2782 16.8637C15.3534 16.8647 15.4276 16.8808 15.4964 16.911C15.5653 16.9412 15.6274 16.9849 15.6791 17.0395L16.9691 18.3292L19.5716 15.7267C19.6232 15.672 19.6853 15.6283 19.7542 15.5981C19.8231 15.5679 19.8973 15.5519 19.9724 15.5508C20.0476 15.5498 20.1222 15.5639 20.1919 15.5922C20.2616 15.6205 20.3248 15.6625 20.378 15.7156C20.4311 15.7688 20.4731 15.8321 20.5013 15.9018C20.5296 15.9714 20.5436 16.0461 20.5426 16.1212C20.5415 16.1964 20.5254 16.2706 20.4952 16.3395C20.4649 16.4083 20.4212 16.4704 20.3666 16.522L17.3666 19.5224C17.2612 19.6279 17.1182 19.6869 16.9691 19.687Z" fill="#333333"/>
          </svg>
        );
      case 'user':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2.12207C13.5054 2.12215 14.9696 2.61684 16.167 3.5293C17.3642 4.44181 18.2286 5.72239 18.6279 7.17383C19.0272 8.62535 18.9391 10.1679 18.377 11.5645C17.8475 12.8796 16.9254 13.9967 15.7393 14.7656C18.229 15.6451 20.3582 17.3964 21.7549 19.8096L21.7969 19.8857C21.8348 19.9643 21.8608 20.0482 21.874 20.1348C21.8916 20.2501 21.8861 20.3683 21.8574 20.4814C21.8287 20.5947 21.7775 20.7017 21.707 20.7949C21.6365 20.8881 21.5474 20.966 21.4463 21.0244C21.3453 21.0827 21.2338 21.1202 21.1182 21.1348C21.0022 21.1493 20.8839 21.1411 20.7715 21.1094C20.659 21.0777 20.5542 21.0231 20.4629 20.9502C20.3717 20.8773 20.296 20.7871 20.2402 20.6846V20.6836C18.4955 17.671 15.4151 15.8751 12 15.875C8.58344 15.875 5.50223 17.6727 3.75781 20.6875L3.75684 20.6865C3.70122 20.7881 3.62662 20.8779 3.53613 20.9502C3.44486 21.0231 3.33998 21.0777 3.22754 21.1094C3.11522 21.141 2.99762 21.1492 2.88184 21.1348C2.76594 21.1203 2.65388 21.0828 2.55273 21.0244C2.45156 20.966 2.36249 20.8881 2.29199 20.7949C2.22151 20.7018 2.1713 20.5947 2.14258 20.4814C2.11391 20.3683 2.10746 20.2502 2.125 20.1348C2.1382 20.0482 2.1642 19.9642 2.20215 19.8857L2.24414 19.8096C3.64075 17.3955 5.77006 15.6443 8.25977 14.7656C7.07373 13.9967 6.1515 12.8796 5.62207 11.5645C5.05994 10.1679 4.97184 8.62534 5.37109 7.17383C5.77047 5.72221 6.63552 4.44183 7.83301 3.5293C9.0305 2.61677 10.4944 2.12207 12 2.12207ZM12.999 3.97266C12.005 3.77505 10.9744 3.87682 10.0381 4.26465C9.10171 4.65256 8.30138 5.30961 7.73828 6.15234C7.17516 6.99513 6.875 7.98639 6.875 9C6.87659 10.3586 7.41632 11.6614 8.37695 12.6221C9.33779 13.5829 10.6412 14.1235 12 14.125C13.0135 14.1249 14.004 13.8238 14.8467 13.2607C15.6894 12.6976 16.3465 11.8974 16.7344 10.9609C17.1222 10.0245 17.2241 8.99408 17.0264 8C16.8287 7.00602 16.3406 6.09267 15.624 5.37598C14.9073 4.65923 13.9932 4.17041 12.999 3.97266Z" fill="#333333" stroke="#333333" strokeWidth="0.25"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const renderFileItem = (fileName = 'Choose file.png') => (
    <div className="file-item">
      <div className="file-info">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.63004 8.13828L3.2487 12.4183C3.0567 12.6583 3.2487 12.9983 3.55804 12.9983H10.2994C10.6194 12.9983 10.8007 12.6583 10.6087 12.4183L7.2487 8.13828C7.09937 7.93828 6.77937 7.93828 6.63004 8.13828Z" fill="#969696"/>
          <path d="M10.3707 10.0901L8.34399 12.6601C8.22666 12.8001 8.34399 13.0101 8.52532 13.0101H12.568C12.76 13.0101 12.8667 12.8101 12.7493 12.6601L10.7333 10.0901C10.7121 10.0622 10.6841 10.0395 10.6516 10.0238C10.6192 10.0082 10.5832 10 10.5467 10C10.5101 10 10.4741 10.0082 10.4417 10.0238C10.4092 10.0395 10.3812 10.0622 10.36 10.0901H10.3707Z" fill="#969696"/>
          <path d="M10.6682 9C11.2573 9 11.7349 8.55228 11.7349 8C11.7349 7.44772 11.2573 7 10.6682 7C10.0791 7 9.60156 7.44772 9.60156 8C9.60156 8.55228 10.0791 9 10.6682 9Z" fill="#969696"/>
          <path d="M13.3292 16H2.6625C1.77717 16 1.0625 15.33 1.0625 14.5V1.5C1.0625 0.67 1.77717 0 2.6625 0H10.2252C10.6518 0 11.0572 0.16 11.3558 0.44L14.4598 3.35C14.7585 3.63 14.9292 4.01 14.9292 4.41V14.5C14.9292 15.33 14.2145 16 13.3292 16ZM2.6625 1C2.36383 1 2.12917 1.22 2.12917 1.5V14.5C2.12917 14.78 2.36383 15 2.6625 15H13.3292C13.6278 15 13.8625 14.78 13.8625 14.5V4.41C13.8634 4.34442 13.8496 4.27941 13.822 4.21913C13.7944 4.15885 13.7537 4.10465 13.7025 4.06L10.5985 1.15C10.5 1.05579 10.3658 1.00188 10.2252 1H2.6625Z" fill="#969696"/>
          <path d="M14.2736 5.00109H11.1696C10.3056 5.00109 9.60156 4.34109 9.60156 3.53109V0.621093C9.60156 0.341094 9.83623 0.121094 10.1349 0.121094C10.4336 0.121094 10.6682 0.341094 10.6682 0.621093V3.53109C10.6682 3.79109 10.8922 4.00109 11.1696 4.00109H14.2736C14.5722 4.00109 14.8069 4.22109 14.8069 4.50109C14.8069 4.78109 14.5722 5.00109 14.2736 5.00109Z" fill="#969696"/>
        </svg>
        <span className="file-name">{fileName}</span>
      </div>
      <button className="delete-file-btn">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="20" height="20" rx="4" fill="#FFDEDE"/>
          <path d="M7.0835 15.25C6.76266 15.25 6.48811 15.1359 6.25983 14.9076C6.03155 14.6793 5.91722 14.4046 5.91683 14.0833V6.5H5.3335V5.33333H8.25016V4.75H11.7502V5.33333H14.6668V6.5H14.0835V14.0833C14.0835 14.4042 13.9694 14.6789 13.7411 14.9076C13.5128 15.1362 13.2381 15.2504 12.9168 15.25H7.0835ZM12.9168 6.5H7.0835V14.0833H12.9168V6.5ZM8.25016 12.9167H9.41683V7.66667H8.25016V12.9167ZM10.5835 12.9167H11.7502V7.66667H10.5835V12.9167Z" fill="#FF0000"/>
        </svg>
      </button>
    </div>
  );

  const renderUploadField = (label = 'Upload', sizeInfo = 'Maximum file size: 64MB (Image Size: 1600x150)') => (
    <div className="upload-field">
      <span className="upload-label">{label}</span>
      <div className="upload-controls">
        <div className="upload-btn">
          <div className="upload-btn-content">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.33464 10.6641V5.23073L5.6013 6.96406L4.66797 5.9974L8.0013 2.66406L11.3346 5.9974L10.4013 6.96406L8.66797 5.23073V10.6641H7.33464ZM4.0013 13.3307C3.63464 13.3307 3.32086 13.2003 3.05997 12.9394C2.79908 12.6785 2.66841 12.3645 2.66797 11.9974V9.9974H4.0013V11.9974H12.0013V9.9974H13.3346V11.9974C13.3346 12.3641 13.2042 12.6781 12.9433 12.9394C12.6824 13.2007 12.3684 13.3312 12.0013 13.3307H4.0013Z" fill="#333333"/>
            </svg>
            <span>Choose file</span>
          </div>
          <span className="upload-hint">Upload any attachment</span>
        </div>
        <span className="upload-size-info">{sizeInfo}</span>
      </div>
    </div>
  );

  return (
    <div className="project-input-page">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <img 
            src="/logo.png?v=2" 
            alt="Conflux" 
            className="logo"
          />
          <div className="header-right">
            <div className="language-selector">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.58333 2.15234C7.93516 2.15234 6.32399 2.64109 4.95358 3.55676C3.58317 4.47244 2.51507 5.77393 1.88434 7.29665C1.25361 8.81936 1.08858 10.4949 1.41013 12.1114C1.73167 13.7279 2.52534 15.2128 3.69078 16.3782C4.85622 17.5437 6.34107 18.3373 7.95758 18.6589C9.57409 18.9804 11.2496 18.8154 12.7724 18.1847C14.2951 17.5539 15.5966 16.4858 16.5122 15.1154C17.4279 13.745 17.9167 12.1339 17.9167 10.4857C17.9141 8.27632 17.0353 6.15818 15.4731 4.59593C13.9108 3.03368 11.7927 2.15489 9.58333 2.15234ZM16.6042 9.84465H13.4127C13.2692 6.91917 12.1426 4.85026 11.2188 3.6267C12.6533 3.97118 13.944 4.756 14.9099 5.87111C15.8759 6.98621 16.4686 8.37567 16.605 9.84465H16.6042ZM7.03766 11.1267H12.129C11.9439 14.4608 10.3478 16.5041 9.58333 17.2966C8.81811 16.5033 7.22276 14.46 7.03766 11.1267ZM7.03766 9.84465C7.22276 6.51052 8.81891 4.46725 9.58333 3.67478C10.3486 4.47045 11.9439 6.51372 12.129 9.84465H7.03766ZM7.94792 3.6267C7.02404 4.85026 5.89744 6.91917 5.75401 9.84465H2.5617C2.69808 8.37567 3.29079 6.98621 4.25673 5.87111C5.22268 4.756 6.5134 3.97118 7.94792 3.6267ZM2.5617 11.1267H5.75401C5.89984 14.0522 7.02404 16.1211 7.94792 17.3446C6.5134 17.0002 5.22268 16.2153 4.25673 15.1002C3.29079 13.9851 2.69808 12.5957 2.5617 11.1267ZM11.2155 17.3446C12.1394 16.1211 13.2636 14.0522 13.4095 11.1267H16.6018C16.4656 12.5953 15.8733 13.9844 14.908 15.0995C13.9427 16.2145 12.6527 16.9996 11.2188 17.3446H11.2155Z" fill="#354556"/>
              </svg>
              <span>عربي</span>
            </div>
            <div className="user-profile">
              <div className="user-avatar"></div>
              <div className="user-info">
                <span className="company-name">Eiffage Construction</span>
                <div className="user-role">
                  <span>Become a Buyer</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 6.33398L8 10.334L4 6.33398H12Z" fill="#6D6D6D" stroke="#6D6D6D" strokeWidth="2" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="dashboard-container">
        {/* Sidebar */}
        <aside className="dashboard-sidebar">
          <div className="sidebar-profile">
            <div className="profile-image-container">
              <div className="profile-image"></div>
              <div className="profile-edit-btn">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="32" height="32" rx="16" fill="#8AA5B4"/>
                  <path d="M16 24H23" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20.0389 9.34106C20.3642 9.01576 20.8054 8.83301 21.2654 8.83301C21.4932 8.83301 21.7188 8.87787 21.9292 8.96505C22.1397 9.05222 22.3309 9.17999 22.492 9.34106C22.653 9.50213 22.7808 9.69335 22.868 9.9038C22.9551 10.1142 23 10.3398 23 10.5676C23 10.7954 22.9551 11.0209 22.868 11.2314C22.7808 11.4418 22.653 11.6331 22.492 11.7941L12.2708 22.0153L9 22.833L9.81769 19.5622L20.0389 9.34106Z" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className="profile-info">
              <span className="profile-name">Architecture Engineer</span>
            </div>
          </div>
          <nav className="sidebar-nav">
            {sidebarMenuItems.map((item) => (
              <div 
                key={item.id} 
                className={`nav-item ${item.id === activeMenu ? 'active' : ''}`}
                onClick={() => setActiveMenu(item.id)}
              >
                <div className="nav-item-content">
                  {renderIcon(item.icon)}
                  <span>{item.label}</span>
                </div>
                {item.hasSubmenu && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="chevron-icon">
                    <path d="M9 6L15 12L9 18" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            ))}
            <div className="nav-item logout">
              <div className="nav-item-content">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.5 3.751C5.974 3.751 5.25 4.381 5.25 5.572V18.431C5.25 19.623 5.974 20.252 6.5 20.252H13.496C13.6949 20.252 13.8857 20.331 14.0263 20.4717C14.167 20.6123 14.246 20.8031 14.246 21.002C14.246 21.2009 14.167 21.3917 14.0263 21.5323C13.8857 21.673 13.6949 21.752 13.496 21.752H6.5C4.817 21.752 3.75 20.079 3.75 18.431V5.571C3.75 3.923 4.817 2.25 6.5 2.25H13.5C13.6989 2.25 13.8897 2.32902 14.0303 2.46967C14.171 2.61032 14.25 2.80109 14.25 3C14.25 3.19891 14.171 3.38968 14.0303 3.53033C13.8897 3.67098 13.6989 3.75 13.5 3.75L6.5 3.751Z" fill="#FF7474"/>
                  <path d="M16.53 7.97325C16.3894 7.8328 16.1988 7.75391 16 7.75391C15.8012 7.75391 15.6106 7.8328 15.47 7.97325V11.2492H9.5C9.30109 11.2492 9.11032 11.3283 8.96967 11.4689C8.82902 11.6096 8.75 11.8003 8.75 11.9992C8.75 12.1982 8.82902 12.3889 8.96967 12.5296C9.11032 12.6702 9.30109 12.7492 9.5 12.7492H15.47V16.0332C15.6106 16.1737 15.8012 16.2526 16 16.2526C16.1988 16.2526 16.3894 16.1737 16.53 16.0332L20.03 12.5332C20.0999 12.4634 20.1553 12.3805 20.1931 12.2892C20.2308 12.1979 20.2502 12.1 20.25 12.0012V11.9992C20.2499 11.8896 20.2258 11.7812 20.1793 11.6819C20.1329 11.5825 20.0652 11.4946 19.981 11.4242L16.53 7.97325Z" fill="#FF7474"/>
                </svg>
                <span>Logout</span>
              </div>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          <div className="content-wrapper">
            {/* Page Header */}
            <div className="page-header">
              <h1 className="page-title">{editId ? 'Edit Project' : 'Project Listing'}</h1>
              <button className="edit-btn">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 20H21" stroke="#2B2B2B" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16.5 3.50023C16.8978 3.1024 17.4374 2.87891 18 2.87891C18.2786 2.87891 18.5544 2.93378 18.8118 3.04038C19.0692 3.14699 19.303 3.30324 19.5 3.50023C19.697 3.69721 19.8532 3.93106 19.9598 4.18843C20.0665 4.4458 20.1213 4.72165 20.1213 5.00023C20.1213 5.2788 20.0665 5.55465 19.9598 5.81202C19.8532 6.06939 19.697 6.30324 19.5 6.50023L7 19.0002L3 20.0002L4 16.0002L16.5 3.50023Z" stroke="#2B2B2B" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Edit</span>
              </button>
            </div>

            {/* Progress Steps */}
            <div className="progress-steps">
              <div className="steps-container">
                <div className={`step ${currentStep >= 1 ? 'completed' : ''}`}>
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="20" cy="20" r="19.5" stroke="#1F396C" strokeDasharray="5.11 5.11"/>
                    <circle cx="20.001" cy="20.001" r="14.4681" fill="#1F396C" stroke="white" strokeWidth="0.851064"/>
                    <path d="M27.2352 15.3208L17.2884 24.6825L12.7671 20.4272" stroke="white" strokeWidth="1.70213" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="step-line"></div>
                <div className={`step ${currentStep >= 2 ? 'completed' : 'inactive'}`}>
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="20" cy="20" r="19.5" stroke="#BFBFBF" strokeDasharray="5.11 5.11"/>
                    <circle cx="20.001" cy="20.001" r="14.8936" fill="#BFBFBF"/>
                    <path d="M27.2352 15.3208L17.2884 24.6825L12.7671 20.4272" stroke="white" strokeWidth="1.70213" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="step-line"></div>
                <div className={`step ${currentStep >= 3 ? 'completed' : 'inactive'}`}>
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="20" cy="20" r="19.5" stroke="#BFBFBF" strokeDasharray="5.11 5.11"/>
                    <circle cx="20.001" cy="20.001" r="14.4681" fill="#BFBFBF" stroke="white" strokeWidth="0.851064"/>
                    <path d="M27.2352 15.3208L17.2884 24.6825L12.7671 20.4272" stroke="white" strokeWidth="1.70213" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className="steps-labels">
                <span className={`step-label ${currentStep >= 1 ? 'active' : ''}`}>1</span>
                <span className={`step-label ${currentStep >= 2 ? 'active' : ''}`}>2</span>
                <span className={`step-label ${currentStep >= 3 ? 'active' : ''}`}>Publish</span>
              </div>
            </div>

            {/* Form Sections */}
            <div className="form-sections">
              {/* Basic Information - Categories */}
              <div className="form-section">
                <h2 className="section-title">Basic Information</h2>
                <div className="form-card">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Category</label>
                      <select
                        className="form-select"
                        value={formData.category}
                        onChange={(e) => {
                          handleInputChange('category', e.target.value);
                          handleInputChange('subCategory', '');
                        }}
                      >
                        <option value="">Select Category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.name}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Sub category</label>
                      <select
                        className="form-select"
                        value={formData.subCategory}
                        onChange={(e) => handleInputChange('subCategory', e.target.value)}
                        disabled={!formData.category}
                      >
                        <option value="">Select Sub category</option>
                        {subcategories.map(sub => (
                          <option key={sub.id} value={sub.name}>{sub.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Basic Information - Project Details */}
              <div className="form-section">
                <h2 className="section-title">Basic Information</h2>
                <div className="form-card">
                  <div className="form-group full-width">
                    <label className="form-label">Project Title</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder="Project Title"
                      value={formData.projectTitle}
                      onChange={(e) => handleInputChange('projectTitle', e.target.value)}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label className="form-label">Sub Title</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder="Sub Title"
                      value={formData.subTitle}
                      onChange={(e) => handleInputChange('subTitle', e.target.value)}
                    />
                  </div>
                  
                  {/* Additional Information */}
                  <div className="additional-info-section">
                    <h3 className="subsection-title">Add more basic information</h3>
                    <div className="additional-info-card">
                      {formData.additionalInfo.map((item, index) => (
                        <div key={item.id} className="additional-info-row">
                          <div className="form-group">
                            <label className="form-label">Title</label>
                            <input 
                              type="text" 
                              className="form-input" 
                              placeholder="Write title name"
                              value={item.title}
                              onChange={(e) => handleAdditionalInfoChange(item.id, 'title', e.target.value)}
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Value</label>
                            <input 
                              type="text" 
                              className="form-input" 
                              placeholder="Write value"
                              value={item.value}
                              onChange={(e) => handleAdditionalInfoChange(item.id, 'value', e.target.value)}
                            />
                          </div>
                          <button 
                            className="delete-row-btn"
                            onClick={() => removeAdditionalInfo(item.id)}
                          >
                            <svg width="28" height="30" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect width="28" height="29.5" rx="4" fill="#FF0000" fillOpacity="0.2"/>
                              <path d="M22.25 8H18.5V7.25C18.5 6.65326 18.2629 6.08097 17.841 5.65901C17.419 5.23705 16.8467 5 16.25 5H11.75C11.1533 5 10.581 5.23705 10.159 5.65901C9.73705 6.08097 9.5 6.65326 9.5 7.25V8H5.75C5.55109 8 5.36032 8.07902 5.21967 8.21967C5.07902 8.36032 5 8.55109 5 8.75C5 8.94891 5.07902 9.13968 5.21967 9.28033C5.36032 9.42098 5.55109 9.5 5.75 9.5H6.5V23C6.5 23.3978 6.65804 23.7794 6.93934 24.0607C7.22064 24.342 7.60218 24.5 8 24.5H20C20.3978 24.5 20.7794 24.342 21.0607 24.0607C21.342 23.7794 21.5 23.3978 21.5 23V9.5H22.25C22.4489 9.5 22.6397 9.42098 22.7803 9.28033C22.921 9.13968 23 8.94891 23 8.75C23 8.55109 22.921 8.36032 22.7803 8.21967C22.6397 8.07902 22.4489 8 22.25 8ZM11 7.25C11 7.05109 11.079 6.86032 11.2197 6.71967C11.3603 6.57902 11.5511 6.5 11.75 6.5H16.25C16.4489 6.5 16.6397 6.57902 16.7803 6.71967C16.921 6.86032 17 7.05109 17 7.25V8H11V7.25ZM20 23H8V9.5H20V23ZM12.5 13.25V19.25C12.5 19.4489 12.421 19.6397 12.2803 19.7803C12.1397 19.921 11.9489 20 11.75 20C11.5511 20 11.3603 19.921 11.2197 19.7803C11.079 19.6397 11 19.4489 11 19.25V13.25C11 13.0511 11.079 12.8603 11.2197 12.7197C11.3603 12.579 11.5511 12.5 11.75 12.5C11.9489 12.5 12.1397 12.579 12.2803 12.7197C12.421 12.8603 12.5 13.0511 12.5 13.25ZM17 13.25V19.25C17 19.4489 16.921 19.6397 16.7803 19.7803C16.6397 19.921 16.4489 20 16.25 20C16.0511 20 15.8603 19.921 15.7197 19.7803C15.579 19.6397 15.5 19.4489 15.5 19.25V13.25C15.5 13.0511 15.579 12.8603 15.7197 12.7197C15.8603 12.579 16.0511 12.5 16.25 12.5C16.4489 12.5 16.6397 12.579 16.7803 12.7197C16.921 12.8603 17 13.0511 17 13.25Z" fill="#FF0000"/>
                            </svg>
                          </button>
                        </div>
                      ))}
                      <button className="add-new-btn" onClick={addAdditionalInfo}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 5V19" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M5 12H19" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Add New</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Image Section */}
              <div className="form-section">
                <h2 className="section-title">Image</h2>
                <div className="form-card">
                  {/* Project Main Image */}
                  <div className="image-upload-section">
                    <h3 className="subsection-title">Project main image</h3>
                    <div className="image-upload-content">
                      <div className="file-list">
                        {Array(8).fill(null).map((_, index) => (
                          <div key={index} className="file-item-wrapper">
                            {renderFileItem()}
                          </div>
                        ))}
                      </div>
                      {renderUploadField()}
                    </div>
                  </div>

                  {/* Project Gallery */}
                  <div className="image-upload-section">
                    <h3 className="subsection-title">
                      Project Gallery
                      <span className="subtitle-note">( Maximum 16 image)</span>
                    </h3>
                    <div className="image-upload-content">
                      <div className="file-list">
                        {Array(8).fill(null).map((_, index) => (
                          <div key={index} className="file-item-wrapper">
                            {renderFileItem()}
                          </div>
                        ))}
                      </div>
                      {renderUploadField()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Next Button */}
              <div className="form-actions">
                {currentStep > 1 && (
                  <button className="back-btn" onClick={() => setCurrentStep(prev => prev - 1)}>
                    <span>Back</span>
                  </button>
                )}
                {currentStep < 3 ? (
                  <button className="next-btn" onClick={() => setCurrentStep(prev => prev + 1)}>
                    <span>Next</span>
                  </button>
                ) : (
                  <button className="next-btn" onClick={handleSubmit} disabled={saving}>
                    <span>{saving ? 'Saving...' : (editId ? 'Update' : 'Publish')}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Upgrade Prompt Modal */}
      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          navigate(-1); // Go back when closing if no permission
        }}
        feature="Submit Projects"
        currentPlan={planDisplayName || 'Basic'}
        requiredPlan="Global Enterprise"
      />
    </div>
  );
};

export default ProjectInputPage;
